import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    Alert,
    Button,
    Card,
    Drawer,
    Dropdown,
    Empty,
    Input,
    Popconfirm,
    Progress,
    Segmented,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
    message
} from 'antd';
import {
    AudioOutlined,
    CopyOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EyeOutlined,
    InboxOutlined,
    PauseOutlined,
    RedoOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';

const { Title, Text, Paragraph } = Typography;

const API = '/api/transcriptions';
const ACCEPT = '.mp3,.ogg,.oga,.opus,.m4a,.aac,.wav,.flac,.wma,.amr,.3gp,.webm,.mp4,.mov,.mkv,.avi';
const MAX_SIZE = 2 * 1024 * 1024 * 1024;

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const errorText = error => error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Ошибка';

const formatSize = bytes => {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} МБ`;
    return `${Math.max(1, Math.round(n / 1024))} КБ`;
};

const formatClock = sec => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const rest = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${rest}` : `${m}:${rest}`;
};

const formatDate = value =>
    value
        ? new Date(value).toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
          })
        : '';

const STATUS = {
    uploading: { color: 'default', label: 'Загружается' },
    uploaded: { color: 'blue', label: 'Загружено' },
    queued: { color: 'gold', label: 'В очереди' },
    processing: { color: 'processing', label: 'Обрабатывается' },
    done: { color: 'green', label: 'Готово' },
    error: { color: 'red', label: 'Ошибка' }
};

const uploadPercent = job => {
    const size = Number(job.size) || 0;
    if (!size) return 0;
    return Math.min(100, Math.round((Number(job.uploaded_bytes) / size) * 100));
};

const saveBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const fileNameFromHeader = (header, fallback) => {
    const match = /filename\*=UTF-8''([^;]+)/i.exec(header || '');
    return match ? decodeURIComponent(match[1]) : fallback;
};

const recorderMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];
    return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
};

const fileExt = mime => {
    const value = String(mime || '');
    if (value.includes('mp4') || value.includes('m4a') || value.includes('aac')) return 'm4a';
    if (value.includes('ogg')) return 'ogg';
    if (value.includes('wav')) return 'wav';
    return 'webm';
};

const recordingName = ext => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `Запись ${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()} ${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}.${ext}`;
};

const RecorderPanel = ({ onRecorded }) => {
    const onRecordedRef = useRef(onRecorded);
    onRecordedRef.current = onRecorded;
    const [phase, setPhase] = useState('idle');
    const [canPause, setCanPause] = useState(false);
    const [seconds, setSeconds] = useState(0);
    const [level, setLevel] = useState(0);
    const [error, setError] = useState('');
    const phaseRef = useRef('idle');
    const discardRef = useRef(false);
    const chunksRef = useRef([]);
    const recRef = useRef(null);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const timerRef = useRef(null);
    const rafRef = useRef(null);

    const release = useCallback(() => {
        clearInterval(timerRef.current);
        timerRef.current = null;
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        const ctx = audioCtxRef.current;
        audioCtxRef.current = null;
        ctx?.close().catch(() => {});
        recRef.current = null;
    }, []);

    useEffect(() => {
        return () => {
            discardRef.current = true;
            const rec = recRef.current;
            if (rec && rec.state !== 'inactive') {
                try {
                    rec.stop();
                } catch (_) {
                    /* запись уже остановлена */
                }
            }
            release();
        };
    }, [release]);

    const watchLevel = stream => {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const data = new Uint8Array(analyser.fftSize);
        const tick = () => {
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i += 1) {
                const v = (data[i] - 128) / 128;
                sum += v * v;
            }
            const next = Math.min(1, Math.sqrt(sum / data.length) * 4);
            setLevel(phaseRef.current === 'recording' ? next : 0);
            rafRef.current = requestAnimationFrame(tick);
        };
        ctx.resume().catch(() => {});
        tick();
    };

    const start = async () => {
        setError('');
        if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setError('Этот браузер не записывает звук. Откройте страницу в Chrome или Safari по защищённому адресу.');
            return;
        }
        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 }
            });
        } catch (err) {
            if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
                setError('Нет доступа к микрофону. Разрешите его в браузере и нажмите «Записать» ещё раз.');
            } else if (err?.name === 'NotFoundError') {
                setError('Микрофон не найден.');
            } else {
                setError('Не удалось включить микрофон.');
            }
            return;
        }
        const mime = recorderMime();
        let recorder;
        try {
            recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        } catch (_) {
            stream.getTracks().forEach(track => track.stop());
            setError('Браузер не смог начать запись.');
            return;
        }
        discardRef.current = false;
        chunksRef.current = [];
        streamRef.current = stream;
        recRef.current = recorder;
        recorder.ondataavailable = event => {
            if (event.data?.size) chunksRef.current.push(event.data);
        };
        recorder.onstop = () => {
            const chunks = chunksRef.current;
            const type = recorder.mimeType || mime || 'audio/webm';
            release();
            phaseRef.current = 'idle';
            setPhase('idle');
            setSeconds(0);
            setLevel(0);
            if (discardRef.current) return;
            const blob = new Blob(chunks, { type });
            if (blob.size < 800) {
                message.warning('Запись слишком короткая');
                return;
            }
            const ext = fileExt(type);
            onRecordedRef.current(new File([blob], recordingName(ext), { type: type.split(';')[0] || 'audio/webm' }));
        };
        recorder.start(1000);
        setCanPause(typeof recorder.pause === 'function' && typeof recorder.resume === 'function');
        phaseRef.current = 'recording';
        setPhase('recording');
        setSeconds(0);
        timerRef.current = setInterval(() => {
            if (phaseRef.current === 'recording') setSeconds(value => value + 1);
        }, 1000);
        watchLevel(stream);
    };

    const pause = () => {
        const rec = recRef.current;
        if (!rec || rec.state !== 'recording' || typeof rec.pause !== 'function') return;
        rec.pause();
        phaseRef.current = 'paused';
        setPhase('paused');
        setLevel(0);
    };

    const resume = () => {
        const rec = recRef.current;
        if (!rec || rec.state !== 'paused' || typeof rec.resume !== 'function') return;
        rec.resume();
        phaseRef.current = 'recording';
        setPhase('recording');
    };

    const finish = discard => {
        discardRef.current = discard;
        const rec = recRef.current;
        if (!rec || rec.state === 'inactive') {
            release();
            phaseRef.current = 'idle';
            setPhase('idle');
            setSeconds(0);
            setLevel(0);
            return;
        }
        rec.stop();
    };

    return (
        <div>
            {phase === 'idle' ? (
                <Button type="primary" icon={<AudioOutlined />} onClick={start}>
                    Записать с микрофона
                </Button>
            ) : (
                <Space wrap>
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: phase === 'paused' ? '#faad14' : '#ff4d4f',
                            boxShadow: phase === 'recording' ? '0 0 0 4px rgba(255,77,79,0.25)' : 'none',
                            display: 'inline-block'
                        }}
                    />
                    <Text strong>{formatClock(seconds)}</Text>
                    <div style={{ width: 96, height: 8, borderRadius: 4, background: '#f0f0f0', overflow: 'hidden' }}>
                        <div style={{ width: `${Math.round(level * 100)}%`, height: '100%', background: '#ff4d4f' }} />
                    </div>
                    {canPause && phase === 'recording' ? (
                        <Button icon={<PauseOutlined />} onClick={pause}>
                            Пауза
                        </Button>
                    ) : null}
                    {canPause && phase === 'paused' ? (
                        <Button icon={<AudioOutlined />} onClick={resume}>
                            Продолжить
                        </Button>
                    ) : null}
                    <Button type="primary" onClick={() => finish(false)}>
                        Остановить и расшифровать
                    </Button>
                    <Button onClick={() => finish(true)}>Отменить</Button>
                </Space>
            )}
            {error ? <Alert type="warning" showIcon message={error} style={{ marginTop: 8 }} /> : null}
        </div>
    );
};

const uploadFile = async (file, language, onProgress) => {
    const { data } = await axios.post(
        API,
        { name: file.name, size: file.size, mime: file.type, language },
        { headers: authHeaders() }
    );
    const { id, chunk_size: chunkSize, next_chunk: nextChunk } = data.data;
    const total = Math.max(1, Math.ceil(file.size / chunkSize));
    const start = Math.min(total, Math.max(0, Number(nextChunk) || 0));
    if (start > 0) onProgress(Math.round((start / total) * 100), 'uploading');
    for (let index = start; index < total; index += 1) {
        const blob = file.slice(index * chunkSize, Math.min(file.size, (index + 1) * chunkSize));
        for (let attempt = 1; ; attempt += 1) {
            try {
                await axios.put(`${API}/${id}/chunk?index=${index}`, blob, {
                    headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' },
                    timeout: 120000
                });
                break;
            } catch (error) {
                if (attempt >= 8 || error?.response?.status === 400) throw error;
                await sleep(1000 * attempt);
            }
        }
        const percent = Math.round(((index + 1) / total) * 100);
        onProgress(percent, percent === 100 ? 'uploaded' : 'uploading');
    }
    await axios.post(`${API}/${id}/complete`, {}, { headers: authHeaders() });
    return id;
};

const TranscriptionPage = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [canSeeAll, setCanSeeAll] = useState(false);
    const [scope, setScope] = useState('mine');
    const [search, setSearch] = useState('');
    const [language, setLanguage] = useState('ru');
    const [uploads, setUploads] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [audioLoading, setAudioLoading] = useState(false);
    const audioRef = useRef(null);
    const uploadQueueRef = useRef(Promise.resolve());

    const loadJobs = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            try {
                const { data } = await axios.get(API, {
                    headers: authHeaders(),
                    params: { scope, search: search || undefined }
                });
                setJobs(data.data || []);
                setCanSeeAll(Boolean(data.can_see_all));
            } catch (error) {
                if (!silent) message.error(errorText(error));
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [scope, search]
    );

    useEffect(() => {
        loadJobs();
    }, [loadJobs]);

    const active = useMemo(
        () => jobs.some(j => ['uploading', 'uploaded', 'queued', 'processing'].includes(j.status)) || uploads.some(u => u.state === 'uploading' || u.state === 'uploaded'),
        [jobs, uploads]
    );

    useEffect(() => {
        if (!active) return undefined;
        const timer = setInterval(() => loadJobs(true), 4000);
        return () => clearInterval(timer);
    }, [active, loadJobs]);

    const patchUpload = (uid, patch) => setUploads(list => list.map(u => (u.uid === uid ? { ...u, ...patch } : u)));

    const enqueueUpload = file => {
        const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        if (file.size > MAX_SIZE) {
            message.error(`${file.name}: файл больше 2 ГБ`);
            return;
        }
        setUploads(list => [...list, { uid, name: file.name, size: file.size, percent: 0, state: 'waiting' }]);
        const lang = language;
        uploadQueueRef.current = uploadQueueRef.current.then(async () => {
            patchUpload(uid, { state: 'uploading' });
            try {
                await uploadFile(file, lang, (percent, state) => patchUpload(uid, { percent, state: state || 'uploading' }));
                patchUpload(uid, { state: 'done', percent: 100 });
                loadJobs(true);
                setTimeout(() => setUploads(list => list.filter(u => u.uid !== uid)), 3000);
            } catch (error) {
                patchUpload(uid, { state: 'error', error: errorText(error) });
            }
        });
    };

    const openJob = async id => {
        setOpenId(id);
        setDetail(null);
        setAudioUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
        setDetailLoading(true);
        try {
            const { data } = await axios.get(`${API}/${id}`, { headers: authHeaders() });
            setDetail(data.data);
        } catch (error) {
            message.error(errorText(error));
        } finally {
            setDetailLoading(false);
        }
    };

    useEffect(() => {
        if (!openId || !detail) return;
        const row = jobs.find(j => j.id === openId);
        if (!row || (row.status === detail.status && row.progress === detail.progress && row.uploaded_bytes === detail.uploaded_bytes)) return;
        axios
            .get(`${API}/${openId}`, { headers: authHeaders() })
            .then(({ data }) => setDetail(data.data))
            .catch(() => {});
    }, [jobs, openId, detail]);

    const closeJob = () => {
        setOpenId(null);
        setDetail(null);
        setAudioUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const loadAudio = async () => {
        if (!detail) return null;
        if (audioUrl) return audioUrl;
        setAudioLoading(true);
        try {
            const { data } = await axios.get(`${API}/${detail.id}/audio`, {
                headers: authHeaders(),
                responseType: 'blob'
            });
            const url = URL.createObjectURL(data);
            setAudioUrl(url);
            return url;
        } catch (error) {
            message.error(errorText(error));
            return null;
        } finally {
            setAudioLoading(false);
        }
    };

    const seek = async seconds => {
        const url = await loadAudio();
        if (!url) return;
        setTimeout(() => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.currentTime = seconds;
            audio.play().catch(() => {});
        }, 50);
    };

    const exportJob = async (job, format) => {
        try {
            const response = await axios.get(`${API}/${job.id}/export`, {
                headers: authHeaders(),
                params: { format },
                responseType: 'blob'
            });
            const fallback = `${job.original_name.replace(/\.[^.]+$/, '')}.${format === 'srt' ? 'srt' : 'txt'}`;
            saveBlob(response.data, fileNameFromHeader(response.headers['content-disposition'], fallback));
        } catch (error) {
            message.error(errorText(error));
        }
    };

    const retryJob = async job => {
        try {
            await axios.post(`${API}/${job.id}/retry`, {}, { headers: authHeaders() });
            message.success('Задание снова в очереди');
            loadJobs(true);
        } catch (error) {
            message.error(errorText(error));
        }
    };

    const deleteJob = async job => {
        try {
            await axios.delete(`${API}/${job.id}`, { headers: authHeaders() });
            if (openId === job.id) closeJob();
            loadJobs(true);
        } catch (error) {
            message.error(errorText(error));
        }
    };

    const copyText = async () => {
        try {
            await navigator.clipboard.writeText(detail?.text || '');
            message.success('Текст скопирован');
        } catch (_) {
            message.error('Не удалось скопировать');
        }
    };

    const exportMenu = job => ({
        items: [
            { key: 'txt', label: 'Текст (.txt)' },
            { key: 'timed', label: 'Текст с таймкодами (.txt)' },
            { key: 'srt', label: 'Субтитры (.srt)' }
        ],
        onClick: ({ key }) => exportJob(job, key)
    });

    const columns = [
        {
            title: 'Файл',
            dataIndex: 'original_name',
            render: (name, job) => (
                <Space direction="vertical" size={0}>
                    <Button type="link" style={{ padding: 0, height: 'auto', textAlign: 'left', whiteSpace: 'normal' }} onClick={() => openJob(job.id)}>
                        {name}
                    </Button>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatSize(job.size)}
                        {job.language === 'auto' ? ' · автоопределение языка' : ''}
                    </Text>
                </Space>
            )
        },
        {
            title: 'Длительность',
            dataIndex: 'duration',
            width: 120,
            render: value => (value ? formatClock(value) : '—')
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            width: 220,
            render: (status, job) => {
                const meta = STATUS[status] || { color: 'default', label: status };
                if (status === 'uploading' || status === 'processing') {
                    return (
                        <Space direction="vertical" size={0} style={{ width: '100%' }}>
                            <Tag color={meta.color}>{meta.label}</Tag>
                            <Progress percent={status === 'uploading' ? uploadPercent(job) : job.progress} size="small" />
                        </Space>
                    );
                }
                if (status === 'error') {
                    return (
                        <Tooltip title={job.error}>
                            <Tag color={meta.color}>{meta.label}</Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Tag color={meta.color}>
                        {meta.label}
                        {status === 'queued' && job.queue_position ? ` · №${job.queue_position}` : ''}
                    </Tag>
                );
            }
        },
        ...(scope === 'all'
            ? [{ title: 'Автор', dataIndex: 'user_name', width: 180 }]
            : []),
        {
            title: 'Загружен',
            dataIndex: 'created_at',
            width: 140,
            render: formatDate
        },
        {
            title: '',
            key: 'actions',
            width: 170,
            render: (_, job) => (
                <Space size={4}>
                    <Tooltip title="Открыть">
                        <Button icon={<EyeOutlined />} size="small" onClick={() => openJob(job.id)} />
                    </Tooltip>
                    <Dropdown menu={exportMenu(job)} disabled={job.status !== 'done'}>
                        <Button icon={<DownloadOutlined />} size="small" disabled={job.status !== 'done'} />
                    </Dropdown>
                    {['error', 'done'].includes(job.status) ? (
                        <Tooltip title="Расшифровать заново">
                            <Button icon={<RedoOutlined />} size="small" onClick={() => retryJob(job)} />
                        </Tooltip>
                    ) : null}
                    <Popconfirm title="Удалить файл и расшифровку?" okText="Удалить" cancelText="Отмена" onConfirm={() => deleteJob(job)}>
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const segments = Array.isArray(detail?.segments) ? detail.segments : [];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div>
                        <Title level={3} style={{ marginBottom: 4 }}>
                            <AudioOutlined /> Транскрибация
                        </Title>
                        <Text type="secondary">
                            Расшифровка аудио и видео в текст. Можно записать с микрофона или загрузить готовый файл. Распознавание идёт на нашем сервере, файлы никуда не передаются.
                            Примерная скорость — 3 минуты записи за 1 минуту.
                        </Text>
                    </div>

                    <Card size="small">
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Space wrap>
                                <Text>Язык записи:</Text>
                                <Select
                                    value={language}
                                    onChange={setLanguage}
                                    style={{ width: 220 }}
                                    options={[
                                        { value: 'ru', label: 'Русский' },
                                        { value: 'auto', label: 'Определить автоматически' }
                                    ]}
                                />
                            </Space>
                            <RecorderPanel onRecorded={enqueueUpload} />
                            <Upload.Dragger
                                multiple
                                accept={ACCEPT}
                                showUploadList={false}
                                beforeUpload={file => {
                                    enqueueUpload(file);
                                    return Upload.LIST_IGNORE;
                                }}
                            >
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Перетащите файлы сюда или нажмите, чтобы выбрать</p>
                                <p className="ant-upload-hint">
                                    MP3, OGG/OPUS (голосовые), M4A, WAV, FLAC, AMR, а также видео MP4/MOV/WEBM. До 2 ГБ на файл, можно несколько сразу.
                                </p>
                            </Upload.Dragger>
                            {uploads.length ? (
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    {uploads.map(u => (
                                        <div key={u.uid}>
                                            <Text>
                                                {u.name} <Text type="secondary">({formatSize(u.size)})</Text>
                                            </Text>
                                            {u.state === 'error' ? (
                                                <Alert type="error" showIcon message={u.error} style={{ marginTop: 4 }} />
                                            ) : (
                                                <Progress
                                                    percent={u.percent}
                                                    size="small"
                                                    status={u.state === 'done' ? 'success' : 'active'}
                                                    format={p =>
                                                        u.state === 'waiting' ? 'ожидает' : u.state === 'uploaded' ? 'Загружено' : `Загружается ${p}%`
                                                    }
                                                />
                                            )}
                                        </div>
                                    ))}
                                </Space>
                            ) : null}
                        </Space>
                    </Card>

                    <Card
                        size="small"
                        title="Задания"
                        extra={
                            <Space wrap>
                                {canSeeAll ? (
                                    <Segmented
                                        value={scope}
                                        onChange={setScope}
                                        options={[
                                            { value: 'mine', label: 'Мои' },
                                            { value: 'all', label: 'Все' }
                                        ]}
                                    />
                                ) : null}
                                <Input.Search
                                    allowClear
                                    placeholder="Поиск по имени и тексту"
                                    onSearch={value => setSearch(value.trim())}
                                    style={{ width: 260 }}
                                />
                                <Button icon={<ReloadOutlined />} onClick={() => loadJobs()} />
                            </Space>
                        }
                    >
                        <Table
                            rowKey="id"
                            size="small"
                            loading={loading}
                            columns={columns}
                            dataSource={jobs}
                            pagination={{ pageSize: 20, hideOnSinglePage: true }}
                            locale={{ emptyText: <Empty description="Пока нет файлов" /> }}
                        />
                    </Card>
                </Space>
            </div>

            <Drawer
                open={Boolean(openId)}
                onClose={closeJob}
                width={Math.min(900, window.innerWidth)}
                title={detail?.original_name || 'Расшифровка'}
                extra={
                    detail?.status === 'done' ? (
                        <Space>
                            <Button icon={<CopyOutlined />} onClick={copyText}>
                                Копировать
                            </Button>
                            <Dropdown menu={exportMenu(detail)}>
                                <Button icon={<DownloadOutlined />}>Скачать</Button>
                            </Dropdown>
                        </Space>
                    ) : null
                }
            >
                {detailLoading || !detail ? (
                    <Spin />
                ) : (
                    <Space direction="vertical" size={16} style={{ width: '100%' }}>
                        <Space wrap>
                            <Tag color={(STATUS[detail.status] || {}).color}>{(STATUS[detail.status] || {}).label || detail.status}</Tag>
                            {detail.duration ? <Text type="secondary">Длительность {formatClock(detail.duration)}</Text> : null}
                            <Text type="secondary">Загружен {formatDate(detail.created_at)}</Text>
                            {detail.user_name ? <Text type="secondary">· {detail.user_name}</Text> : null}
                        </Space>

                        {audioUrl ? (
                            <audio ref={audioRef} src={audioUrl} controls style={{ width: '100%' }} />
                        ) : (
                            <Button icon={<AudioOutlined />} loading={audioLoading} onClick={loadAudio}>
                                Прослушать запись
                            </Button>
                        )}

                        {detail.status === 'error' ? <Alert type="error" showIcon message="Ошибка расшифровки" description={detail.error} /> : null}
                        {detail.status === 'uploading' ? (
                            <Progress percent={uploadPercent(detail)} status="active" />
                        ) : null}
                        {detail.status === 'uploaded' ? (
                            <Alert type="info" showIcon message="Файл загружен и ждёт обработки." />
                        ) : null}
                        {detail.status === 'queued' ? (
                            <Alert type="info" showIcon message="Файл загружен и стоит в очереди на обработку." />
                        ) : null}
                        {detail.status === 'processing' ? (
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <Alert type="info" showIcon message="Файл обрабатывается — текст появится здесь, когда расшифровка закончится." />
                                <Progress percent={detail.progress || 0} status="active" />
                            </Space>
                        ) : null}

                        {detail.status === 'done' ? (
                            segments.length ? (
                                <div>
                                    {segments.map((seg, index) => (
                                        <div key={index} style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
                                            <Button type="link" size="small" style={{ padding: 0, minWidth: 56 }} onClick={() => seek(seg.start)}>
                                                {formatClock(seg.start)}
                                            </Button>
                                            <Paragraph style={{ margin: 0, flex: 1 }}>{seg.text}</Paragraph>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Empty description="Речь не распознана" />
                            )
                        ) : null}
                    </Space>
                )}
            </Drawer>
        </Layout>
    );
};

export default TranscriptionPage;
